import { useEffect, useRef, useState } from "react";
import { GetFundPerformance } from "../../API/RequestsServices";

const ITEMS_PER_PAGE = 10;

const Performance = ({ toast }) => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const debounceRef = useRef(null);

  const loadFunds = async (page = currentPage, term = search) => {
    setLoading(true);
    setError(false);
    const result = await GetFundPerformance({
      page,
      pageSize: ITEMS_PER_PAGE,
      search: term,
      toast,
    });

    if (result?.error) {
      setError(true);
      setFunds([]);
      setTotalPages(1);
      setTotalRecords(0);
    } else {
      setFunds(result.items || []);
      setTotalPages(result.totalPages || 1);
      setTotalRecords(result.totalRecords || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadFunds(currentPage, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search]);

  // Debounce typing — 400ms after the user stops typing, run search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const formatPercent = (value) => {
    if (value === undefined || value === null) return "-";
    // API returns base 100 (e.g. 117.0259 means +17.0259%)
    const change = value - 100;
    const formatted = change.toFixed(2);
    const isPositive = change >= 0;
    return (
      <span className={isPositive ? "text-green-600" : "text-red-600"}>
        {isPositive ? "+" : ""}
        {formatted}%
      </span>
    );
  };

  const formatDecimal = (value, decimals = 2) => {
    if (value === undefined || value === null) return "-";
    return Number(value).toFixed(decimals);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatAUM = (value) => {
    if (value === undefined || value === null) return "-";
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
    return Number(value).toFixed(2);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Fund Performance
          </h2>
          <button
            onClick={() => loadFunds(currentPage, search)}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
        <div className="rounded-xl border border-dashed border-red-300 bg-white p-6 text-sm text-red-700">
          Couldn't load the performance data. Please try again.
        </div>
      </div>
    );
  }

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + funds.length, totalRecords);

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">
          Fund Performance
        </h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search by ISIN..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={() => loadFunds(currentPage, search)}
            disabled={loading}
            className="px-4 py-1.5 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-60"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      ) : funds.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          No funds found.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  ISIN
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Fund Name
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  AUM
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  NAV Date
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  YTD
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  1 Day
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  1 Month
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  6 Months
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  1 Year
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  3Y Ann. Return
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  3Y Volatility
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  3Y Sortino
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  3Y Max DD
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {funds.map((fund) => (
                <tr key={fund.id ?? fund.isin} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900">
                    {fund.isin || "-"}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700">
                    {fund.fundName || "-"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-700">
                    {formatAUM(fund.aumSize)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-700">
                    {formatDate(fund.latestDate)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {formatPercent(fund.yearToDate)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {formatPercent(fund.oneDayPerformance)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {formatPercent(fund.oneMonth)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {formatPercent(fund.sixMonth)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {formatPercent(fund.oneYear)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-700">
                    {formatDecimal(fund.threeYearsAnnualizedReturn, 4)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-700">
                    {formatDecimal(fund.threeYearsVolatility, 4)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-700">
                    {formatDecimal(fund.threeYearsSortino, 4)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                    {fund.threeYearsMaxDrawDown !== null &&
                    fund.threeYearsMaxDrawDown !== undefined ? (
                      <span className="text-red-600">
                        -
                        {(fund.threeYearsMaxDrawDown * 100).toFixed(2)}%
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-500">
          {totalRecords > 0
            ? `Showing ${startIndex + 1}-${endIndex} of ${totalRecords} funds`
            : ""}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .map((page, idx, arr) => {
                  const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                  return (
                    <span key={page} className="flex items-center gap-1">
                      {showEllipsis && (
                        <span className="px-1 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 text-sm rounded border ${
                          currentPage === page
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : "border-gray-300 bg-white hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    </span>
                  );
                })}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Performance;
